import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Typography,
  Spin,
  message,
  Tooltip,
  Badge,
} from "antd";
import * as XLSX from "xlsx";
import {
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { getInformePlanCompraGeneral } from "../../../services/informes/planComprasSostenibilidadService copy";

const { Title, Text } = Typography;
const { Option } = Select;

const InformePlanComprasGeneral = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [selectedImplementador, setSelectedImplementador] = useState("");

  // Cargar datos
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await getInformePlanCompraGeneral();
      if (response.estado) {
        setData(response.data);
        setFilteredData(response.data);
        message.success("Datos cargados correctamente");
      } else {
        message.error("Error al cargar los datos");
      }
    } catch (error) {
      message.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrar datos
  useEffect(() => {
    let filtered = data;

    if (searchText) {
      filtered = filtered.filter(
        (item) =>
          item.nombre_producto
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          item.codigo.toLowerCase().includes(searchText.toLowerCase()) ||
          item.codigo_interno.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (selectedCategoria) {
      filtered = filtered.filter(
        (item) => item.categoria === selectedCategoria
      );
    }

    if (selectedImplementador) {
      filtered = filtered.filter(
        (item) => item.implementador === selectedImplementador
      );
    }

    setFilteredData(filtered);
  }, [searchText, selectedCategoria, selectedImplementador, data]);

  // Formatear números
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("es-CO").format(value);
  };

  // Obtener opciones únicas para filtros
  const categorias = [...new Set(data.map((item) => item.categoria))];
  const implementadores = [...new Set(data.map((item) => item.implementador))];

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const totalPlanificado = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valorTotal_plan),
      0
    );

    const totalEntregado_sin_iva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_entregado_sin_iva),
      0
    );
    const totalEntregado = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_entregado_con_iva),
      0
    );

    const promedioAvance = (totalEntregado * 100) / totalPlanificado || 0;

    return {
      totalPlanificado,
      totalEntregado,
      totalEntregado_sin_iva,
      promedioAvance,
      totalProductos: filteredData.length,
    };
  };

  const estadisticas = calcularEstadisticas();

  // Exportar a Excel
  const exportToExcel = () => {
    try {
      // Preparar los datos para Excel
      const excelData = filteredData.map((row) => ({
        "Código Interno": row.codigo_interno,
        Implementador: row.implementador,
        Categoría: row.categoria,
        Código: row.codigo,
        "Nombre Producto": row.nombre_producto,
        Unidad: row.unidad,
        "Cantidad Plan": parseFloat(row.cantidad_plan),
        "Valor Unitario Plan": parseFloat(row.valorUnitario_plan),
        "Valor Total Plan": parseFloat(row.valorTotal_plan),
        "Cantidad Asignada": parseInt(row.cantidad_asignada),
        "Cantidad Entregada": parseInt(row.cantidad_entregada),
        "Valor Entregado Sin IVA": parseFloat(row.valor_entregado_sin_iva),
        "Valor Entregado Con IVA": parseFloat(row.valor_entregado_con_iva),
        "Valor total IVA": parseFloat(
          row.valor_entregado_con_iva - row.valor_entregado_sin_iva
        ),
        "Porcentaje Avance": parseFloat(row.porcentaje_avance),
      }));

      // Crear el libro de trabajo
      const workbook = XLSX.utils.book_new();

      // Crear la hoja de trabajo
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 12 }, // Código Interno
        { wch: 15 }, // Implementador
        { wch: 35 }, // Categoría
        { wch: 10 }, // Código
        { wch: 50 }, // Nombre Producto
        { wch: 10 }, // Unidad
        { wch: 12 }, // Cantidad Plan
        { wch: 18 }, // Valor Unitario Plan
        { wch: 18 }, // Valor Total Plan
        { wch: 15 }, // Cantidad Asignada
        { wch: 15 }, // Cantidad Entregada
        { wch: 20 }, // Valor Entregado Sin IVA
        { wch: 20 }, // Valor Entregado Con IVA
        { wch: 20 }, // Valor Total Con IVA
        { wch: 15 }, // Porcentaje Avance
      ];

      worksheet["!cols"] = columnWidths;

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, "Informe Plan Compras");

      // Generar y descargar el archivo
      const fecha = new Date().toISOString().split("T")[0];
      const nombreArchivo = `informe_plan_compras_${fecha}.xlsx`;

      XLSX.writeFile(workbook, nombreArchivo);

      message.success(
        `Archivo Excel "${nombreArchivo}" descargado correctamente`
      );
    } catch (error) {
      console.error("Error al generar el archivo Excel:", error);
      message.error("Error al generar el archivo Excel");
    }
  };

  // Definir columnas de la tabla
  const columns = [
    {
      title: "Codigo proyecto",
      dataIndex: "codigo_interno",
      key: "codigo_interno",
      width: 100,
      fixed: "left",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
        </div>
      ),
    },
    {
      title: "Implementador",
      dataIndex: "implementador",
      key: "implementador",
      width: 120,
      render: (text) => <Badge status="processing" text={text} />,
    },
    {
      title: "Ficha Técnica",
      dataIndex: "codigo",
      key: "codigo",
      width: 100,
      fixed: "left",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
        </div>
      ),
    },
    {
      title: "Producto",
      dataIndex: "nombre_producto",
      key: "nombre_producto",
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: "Categoría",
      dataIndex: "categoria",
      key: "categoria",
      width: 200,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Factor",
      dataIndex: "factor",
      key: "factor",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: "Sub factor",
      dataIndex: "subfactor",
      key: "subfactor",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },

    {
      title: "Plan",
      children: [
        {
          title: "Cantidad",
          dataIndex: "cantidad_plan",
          key: "cantidad_plan",
          width: 100,
          align: "center",

          render: (text, record) => (
            <div>
              <Text>{formatNumber(text)}</Text>
            </div>
          ),
        },
        {
          title: "Unidad Medida",
          dataIndex: "unidad",
          key: "unidad",
          width: 130,
          align: "left",
          ellipsis: {
            showTitle: false,
          },
          render: (text) => (
            <Tooltip placement="topLeft" title={text}>
              {text}
            </Tooltip>
          ),
        },
        {
          title: "Valor unitario",
          dataIndex: "valorUnitario_plan",
          key: "valorUnitario_plan",
          width: 130,
          align: "center",
          render: (text) => <Text strong>{formatCurrency(text)}</Text>,
        },
        {
          title: "Valor Total",
          dataIndex: "valorTotal_plan",
          key: "valorTotal_plan",
          width: 130,
          align: "right",
          render: (text) => <Text strong>{formatCurrency(text)}</Text>,
        },
      ],
    },
    {
      title: "Ejecución",
      children: [
        {
          title: "Asignado",
          dataIndex: "cantidad_asignada",
          key: "cantidad_asignada",
          width: 90,
          align: "center",
          render: (text) => formatNumber(text),
        },
        {
          title: "Entregado",
          dataIndex: "cantidad_entregada",
          key: "cantidad_entregada",
          width: 90,
          align: "center",
          render: (text) => formatNumber(text),
        },
        {
          title: "Valor sin IVA",
          dataIndex: "valor_entregado_sin_iva",
          key: "valor_entregado_sin_iva",
          width: 130,
          align: "right",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Valor con IVA",
          dataIndex: "valor_entregado_con_iva",
          key: "valor_entregado_con_iva",
          width: 130,
          align: "right",
          render: (text) => formatCurrency(text),
        },

        {
          title: "Valor total IVA",
          key: "valor_total_iva",
          width: 130,
          align: "right",
          render: (text, record) =>
            formatCurrency(
              record.valor_entregado_con_iva - record.valor_entregado_sin_iva
            ),
        },
      ],
    },
    {
      title: "Avance",
      dataIndex: "porcentaje_avance",
      key: "porcentaje_avance",
      width: 120,
      align: "center",
      render: (text) => {
        const percentage = text ? parseFloat(text) : 0; // 👈 si es null/vacío -> 0
        let status = "exception";

        if (percentage > 75) status = "success";
        else if (percentage > 25) status = "active";

        return (
          <Progress
            type="line"
            size="default"
            percent={percentage}
            status={status}
            format={() => `${percentage.toFixed(1)}%`}
          />
        );
      },
    },
  ];

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <Title level={2} style={{ marginBottom: "24px", color: "#1890ff" }}>
        📊 Informe de Plan de Compras
      </Title>

      {/* Filtros y controles */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} lg={5}>
            <Select
              placeholder="Filtrar por implementador"
              style={{ width: "100%" }}
              value={selectedImplementador}
              onChange={setSelectedImplementador}
              allowClear
              showSearch
              optionFilterProp="children" // busca dentro del texto de las opciones
            >
              {implementadores.map((implementador) => (
                <Option key={implementador} value={implementador}>
                  {implementador}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Select
              placeholder="Filtrar por categoría"
              style={{ width: "100%" }}
              value={selectedCategoria}
              onChange={setSelectedCategoria}
              allowClear
              showSearch
              optionFilterProp="children" // busca dentro del texto de las opciones
            >
              {categorias.map((categoria) => (
                <Option key={categoria} value={categoria}>
                  {categoria}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Input
              placeholder="Buscar por producto, código..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={loadData}
                loading={loading}
              >
                Actualizar
              </Button>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={exportToExcel}
                disabled={filteredData.length === 0}
                style={{
                  backgroundColor: "#52c41a",
                  color: "#fff",
                  borderColor: "#52c41a",
                }}
              >
                Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      {/* Tarjetas de estadísticas */}
      <Row gutter={16} style={{ marginBottom: "24px" }} align="stretch">
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Statistic
              title="Total Productos"
              value={estadisticas.totalProductos}
              prefix="📦"
              valueStyle={{ color: "#1890ff", fontSize: "18px" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Statistic
              title="Valor Planificado"
              value={estadisticas.totalPlanificado}
              formatter={(value) => formatCurrency(value)}
              prefix="💰"
              valueStyle={{ color: "#52c41a", fontSize: "18px" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered
            bodyStyle={{ padding: "16px" }}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Título principal a la izquierda */}
                <span>💰 Valor Entrante</span>

                {/* Valor IVA a la derecha, subtítulo + Tag */}
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    Valor IVA
                  </div>
                  <Tag
                    color="blue"
                    style={{ fontWeight: 600, fontSize: "14px" }}
                  >
                    {formatCurrency(
                      (estadisticas.totalEntregado ?? 0) -
                        (estadisticas.totalEntregado_sin_iva ?? 0)
                    )}
                  </Tag>
                </div>
              </div>
            }
          >
            {/* Columnas para Sin IVA / Con IVA */}
            <Row gutter={16} justify="center" align="middle">
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title="Sin IVA"
                  value={estadisticas.totalEntregado_sin_iva}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ color: "#52c41a", fontSize: "18px" }}
                />
              </Col>
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title="Con IVA"
                  value={estadisticas.totalEntregado}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ color: "#1890ff", fontSize: "18px" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Statistic
              title="Avance Promedio"
              value={estadisticas.promedioAvance}
              suffix="%"
              precision={1}
              prefix="📈"
              valueStyle={{ color: "#fa8c16", fontSize: "18px" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabla de datos */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="codigo"
            scroll={{ x: 1500, y: 600 }}
            pagination={{
              total: filteredData.length,
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["1", "15", "20", "50"], // 👈 aquí defines las opciones
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} productos`,
            }}
            size="small"
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <Text strong>TOTALES</Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>
                      {new Intl.NumberFormat("es-CO").format(
                        filteredData.reduce(
                          (sum, item) => sum + parseFloat(item.cantidad_plan),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell
                    index={2}
                    colSpan={2}
                  ></Table.Summary.Cell>

                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) => sum + parseFloat(item.valorTotal_plan),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={4} align="right">
                    <Text strong>
                      {new Intl.NumberFormat("es-CO").format(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.cantidad_asignada),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>
                      {new Intl.NumberFormat("es-CO").format(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.cantidad_entregada),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_entregado_sin_iva),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={7} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_entregado_con_iva),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={8} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum +
                            (parseFloat(item.valor_entregado_con_iva || 0) -
                              parseFloat(item.valor_entregado_sin_iva || 0)),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default InformePlanComprasGeneral;
